import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CardGrid } from 'src/components/cardGrid';
import { FilterList } from 'src/components/filterList';
import { Footer } from 'src/components/footer/Footer';
import { Header } from 'src/components/header';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { SimpleDialog } from 'src/components/modal';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Snackbar } from 'src/components/snackbar';
import { StyledTypography } from 'src/components/subHeader/SubHeaderWithSorting.styled';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting, useModal } from 'src/hooks';
import { IComponent, IFilter, IStatus, IStatusLabel } from 'src/interfaces';
import {
  DataTestID,
  getComponentDisplayName,
  lang,
  useUserRole,
} from 'src/utils';

import {
  CheckboxLabel,
  ComponentContainer,
  ComponentSectionHeader,
} from '../addComponentTagPage/AddComponentTagPage.styled';
import { tagNameMaxLength, TestIds } from './constants';
import {
  CardGridWrapper,
  InputContainer,
  InputTextField,
  StyleDivider,
} from './EditComponentTagPage.styled';
import { useEditComponentTagPage } from './hooks/EditComponentTagPage.hook';
import { buildComponentTagAssignmentUpdates } from './modules/EditComponentTagPage.helper';

export const EditComponentTagPage = () => {
  const { tagId = '' } = useParams();
  const [components, setComponents] = useState<IComponent[]>([]);
  const [inputBoxStatus, setInputBoxStatus] = useState('');
  const [componentTagName, setComponentTagName] = useState('');
  const [selectedComponents, setSelectedComponents] = useState<IComponent[]>(
    [],
  );
  const [prevSelectedComponents, setPrevSelectedComponents] = useState<
    IComponent[]
  >([]);
  const gridBottomRef = useRef<HTMLDivElement>(null);
  const [selectAll, setSelectAll] = useState(false);
  const [isSelectAll, setIssSelectAll] = useState(false);
  const [singleSelect, setSingleSelect] = useState(false);

  const navigate = useNavigate();
  const CloseModal = useModal();
  const editConfirmDialog = useModal();
  const { isViewOnly } = useUserRole();

  const [onChangeLoad, setOnChangeLoad] = useState<boolean>(false);

  const initialComponentIdsRef = useRef<Set<number>>(new Set());
  const initialTagNameRef = useRef<string>('');
  const lastTagIdRef = useRef<string>('');
  const lastPersistedComponentIdsRef = useRef<string>('');

  const {
    data,
    hasNextPage,
    fetchNextPage,
    componentTypesPage,
    getNextComponentPage,
    errorLoadingComponents,
    isLoadingComponentTypes,
    errorLoadingComponentTypes,
    isLoadingComponents,
    isFetching,
    isFetchingNextPage,
    onChangeParams,
    updateComponentTag,
    updateComponents,
    selectedComponentTag,
    persistedSelectedComponents,
    isSelectedDataSuccess,
    isComponentSuccess,
    selectedPageData,
    isSelectedPageDataSuccess,
  } = useEditComponentTagPage(Number(tagId));

  useEffect(() => {
    if (isSelectedDataSuccess)
      setPrevSelectedComponents(persistedSelectedComponents ?? []);
  }, [persistedSelectedComponents, isSelectedDataSuccess]);

  useEffect(() => {
    if (tagId !== lastTagIdRef.current) {
      lastTagIdRef.current = tagId;
      initialComponentIdsRef.current = new Set();
      initialTagNameRef.current = '';
      lastPersistedComponentIdsRef.current = '';
    }
  }, [tagId]);

  useEffect(() => {
    const list = persistedSelectedComponents ?? [];
    const idsSignature = list
      .map((c) => c.id)
      .sort((a, b) => a - b)
      .join(',');

    if (idsSignature !== lastPersistedComponentIdsRef.current) {
      lastPersistedComponentIdsRef.current = idsSignature;
      initialComponentIdsRef.current = new Set(list.map((c) => c.id));
    }
  }, [persistedSelectedComponents]);

  useEffect(() => {
    if (selectedComponentTag?.name) {
      if (tagId !== lastTagIdRef.current || !initialTagNameRef.current) {
        initialTagNameRef.current = selectedComponentTag.name;
        setComponentTagName(selectedComponentTag.name);
      }
    }
  }, [selectedComponentTag?.name, tagId]);

  const selectedCount = selectedComponents.length;

  useEffect(() => {
    if (
      !isSelectedPageDataSuccess ||
      !isComponentSuccess ||
      !isSelectedDataSuccess ||
      !data
    )
      return;
    const shouldInstantlyFetchNextPage =
      components.length &&
      gridBottomRef.current &&
      gridBottomRef.current.getBoundingClientRect().bottom <
        window.innerHeight &&
      hasNextPage;

    if (
      (!isSelectAll && !singleSelect) ||
      onChangeLoad ||
      shouldInstantlyFetchNextPage
    ) {
      const selectedIds = new Set(selectedPageData?.map((comp) => comp.id));
      const newlySelectedIds = new Set(
        selectedComponents?.map((comp) => comp.id),
      );
      const hasExtraSelection = [...newlySelectedIds].some(
        (id) => !selectedIds.has(id),
      );

      if (!hasExtraSelection && onChangeLoad) {
        const defaultSelected =
          selectedPageData?.filter((comp) =>
            prevSelectedComponents.some((prev) => prev.id === comp.id),
          ) ?? [];
        setSelectedComponents(defaultSelected);
      }

      const remainingComponents = data.pages.flatMap((page) => page.data);
      setComponents([...(selectedPageData ?? []), ...remainingComponents]);
    }
    setSingleSelect(false);
    setIssSelectAll(false);
    setOnChangeLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    selectedPageData,
    isSelectedPageDataSuccess,
    isComponentSuccess,
    isSelectedDataSuccess,
    prevSelectedComponents,
    onChangeLoad,
  ]);

  useEffect(() => {
    setOnChangeLoad(true);
    if (selectedComponents.length > 0) {
      setSelectedComponents((selectedComponents) => {
        const newComponents = prevSelectedComponents.filter((component) =>
          selectedComponents.some((selected) => selected.id === component.id),
        );

        return newComponents;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChangeParams]);

  useEffect(() => {
    if (components.length && gridBottomRef.current) {
      const { bottom } = gridBottomRef.current.getBoundingClientRect();
      const shouldInstantlyFetchNextPage =
        bottom < window.innerHeight && hasNextPage;

      if (shouldInstantlyFetchNextPage) {
        fetchNextPage();
      }
      setSelectAll(areAllComponentsSelected(components, selectedComponents));
    }
  }, [components, fetchNextPage, hasNextPage, selectedComponents]);

  const handleSelectAllClick = () => {
    const componentIds = new Set(components.map((c) => c.id));
    const selectedDataIds = new Set(
      persistedSelectedComponents?.map((c) => c.id) || [],
    );
    if (selectAll) {
      setSelectedComponents([]);

      setPrevSelectedComponents((prev) =>
        prev.filter(
          (prevComp) =>
            !componentIds.has(prevComp.id) || !selectedDataIds.has(prevComp.id),
        ),
      );
    } else {
      setSelectedComponents(components);

      setPrevSelectedComponents((prev) => {
        const prevIds = new Set(prev.map((c) => c.id));
        const toAdd = components.filter(
          (comp) => selectedDataIds.has(comp.id) && !prevIds.has(comp.id),
        );

        return [...prev, ...toAdd];
      });
    }
    setIssSelectAll(true);
  };

  const handleComponentTagNameChange = useCallback((value: string) => {
    startTransition(() => {
      setComponentTagName(value);

      const trimValue = value.trim();
      const trimInitial = initialTagNameRef.current.trim();
      if (trimValue === trimInitial || trimValue === '') {
        setInputBoxStatus('');
      } else {
        setInputBoxStatus('changed');
      }
    });
  }, []);

  const handleComponentCheckboxChange = useCallback(
    (component: IComponent) => {
      setSingleSelect(true);
      const isSelected = selectedComponents.some(
        (selectedComponent) => selectedComponent.id === component.id,
      );

      const isPrevSelectedComponent = persistedSelectedComponents?.some(
        (selectedComponent) => selectedComponent.id === component.id,
      );

      if (isPrevSelectedComponent && !isSelected) {
        setPrevSelectedComponents((prev) => {
          const exists = prev.some((c) => c.id === component.id);
          if (exists) return prev;

          return [...prev, component];
        });
      } else if (isPrevSelectedComponent && isSelected) {
        setPrevSelectedComponents(
          prevSelectedComponents.filter(
            (prevSelected) => prevSelected.id !== component.id,
          ),
        );
      }

      const updatedSelectedComponents = isSelected
        ? selectedComponents.filter(
            (selectedComponent) => selectedComponent.id !== component.id,
          )
        : [...selectedComponents, component];

      setSelectedComponents(updatedSelectedComponents);
      setSelectAll(
        areAllComponentsSelected(components, updatedSelectedComponents),
      );
    },
    [
      components,
      setSelectedComponents,
      setPrevSelectedComponents,
      prevSelectedComponents,
      selectedComponents,
      persistedSelectedComponents,
      setSelectAll,
    ],
  );

  const areAllComponentsSelected = (
    components: IComponent[],
    selected: IComponent[],
  ) => {
    return components.every((component) =>
      selected.some(
        (selectedComponent) => selectedComponent.id === component.id,
      ),
    );
  };
  const handleModalConfirm = () => {
    navigate(PATHS.COMPONENT_TAGS_MANAGE);
  };

  const hasCheckboxesChanged = useMemo(() => {
    const currentIds = new Set(
      selectedComponents.map((selectedComponent) => selectedComponent.id),
    );
    const initialIds = initialComponentIdsRef.current;

    if (currentIds.size !== initialIds.size) {
      return true;
    }

    return !Array.from(currentIds).every((id) => initialIds.has(id));
  }, [selectedComponents]);

  const hasNameChanged = useMemo(() => {
    const trimmedName = componentTagName.trim();
    const trimInitial = initialTagNameRef.current.trim();
    const duplicateBlocked =
      inputBoxStatus === lang.componentTagModal.uniqueTagAlert;

    return (
      trimmedName !== '' && trimmedName !== trimInitial && !duplicateBlocked
    );
  }, [componentTagName, inputBoxStatus]);

  const isSaveDisabled = useMemo(
    () => !(hasNameChanged || hasCheckboxesChanged),
    [hasNameChanged, hasCheckboxesChanged],
  );

  const isInitialListLoading = useMemo(
    () =>
      isLoadingComponents ||
      !isSelectedDataSuccess ||
      !isSelectedPageDataSuccess ||
      !isComponentSuccess,
    [
      isLoadingComponents,
      isSelectedDataSuccess,
      isSelectedPageDataSuccess,
      isComponentSuccess,
    ],
  );

  const isListLoading = useMemo(
    () => isInitialListLoading || (isFetching && components.length === 0),
    [isInitialListLoading, isFetching, components.length],
  );

  const isSaving = updateComponentTag.isLoading || updateComponents.isLoading;

  const handleSave = async () => {
    const componentTagForSave = componentTagName.trim();

    try {
      if (hasNameChanged) {
        const request = {
          componentTagId: tagId,
          componentTag: componentTagForSave,
        };
        await updateComponentTag.mutateAsync(request);
      }

      if (hasCheckboxesChanged) {
        const updatedComponents = buildComponentTagAssignmentUpdates(
          selectedComponents,
          persistedSelectedComponents,
          Number(tagId),
        );

        if (updatedComponents.length > 0) {
          await updateComponents.mutateAsync(updatedComponents);
        }
      }

      if (hasNameChanged || hasCheckboxesChanged) {
        navigate(PATHS.COMPONENT_TAGS_MANAGE);
        Snackbar.success(`${componentTagForSave} ${lang.successfullyUpdated}`);
      }
    } catch (error: any) {
      if (
        error.response?.data?.error?.includes(
          'An item with the same unique field already exists',
        )
      ) {
        setInputBoxStatus(lang.componentTagModal.uniqueTagAlert);
      } else {
        Snackbar.error(lang.failedSaveText);
      }
    }
  };

  const filters: IFilter[] = [
    {
      label: 'Component type',
      options: componentTypesPage?.data.map((componentType) => ({
        value: componentType.id,
        label: getComponentDisplayName(componentType.name),
      })),
      totalPages: componentTypesPage?.totalPages,
      currPage: componentTypesPage.page,
      isLoading: isLoadingComponentTypes,
      isError: errorLoadingComponentTypes ? true : false,
    },
    {
      label: 'Status',
      options: Object.values(IStatus).map((value) => ({
        value: value,
        label: IStatusLabel[`${value}`],
      })),
    },
    {
      label: lang.componentTags,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'primary',
      },
    },
  ];

  const { onChangeKeyword, onRemoveKeyword, onReset } =
    useFilterAndSorting(filters);

  const totalCount =
    (data?.pages?.[0]?.totalCount ?? 0) + (selectedPageData?.length ?? 0);

  let numberofResults =
    data?.pages?.reduce((total, page) => {
      return total + (page.data?.length || 0);
    }, 0) || 0;
  numberofResults += selectedPageData?.length || 0;

  const shouldDisplayResultInfo = numberofResults != null && totalCount != null;
  const resultCountText = `${lang.Showing} ${numberofResults}/${totalCount}`;

  return (
    <Grid>
      <Header
        backLink={-1}
        titlePrefix={lang.edit}
        title={componentTagName}
        breadcrumbItems={[
          {
            label: lang.components,
            link: `${PATHS.COMPONENTS}`,
          },
          {
            label: lang.tags,
            link: `${PATHS.COMPONENT_TAGS_MANAGE}`,
          },
          {
            label: lang.editTag,
          },
        ]}
        actionBar={isViewOnly && <ViewOnlyAlert />}
      />
      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <FilterList
            filters={filters}
            onChangeKeyword={onChangeKeyword}
            onRemoveKeyword={onRemoveKeyword}
            onChangeFilter={onChangeParams}
            showReset
            onReset={onReset}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <Typography variant="h4" component="h2" gutterBottom>
            Tag details
          </Typography>
          <StyleDivider />
          <InputContainer>
            <InputTextLabel>{lang.tagName}</InputTextLabel>
            <InputTextField
              variant="outlined"
              inputProps={{
                maxLength: tagNameMaxLength,
                'data-testid': TestIds.TAG_NAME_INPUT,
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleComponentTagNameChange(event.target.value)
              }
              onBlur={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleComponentTagNameChange(event.target.value)
              }
              value={componentTagName}
              error={
                !!inputBoxStatus &&
                inputBoxStatus !== 'changed' &&
                inputBoxStatus !== ''
              }
              helperText={inputBoxStatus === 'changed' ? '' : inputBoxStatus}
            />
            {shouldDisplayResultInfo && (
              <StyledTypography
                paddingTop={4}
                data-testid={DataTestID.ComponentTagCount}>
                {resultCountText}
              </StyledTypography>
            )}
          </InputContainer>
          <ComponentContainer>
            {components.length > 0 && !isListLoading && (
              <ComponentSectionHeader>
                <Typography variant="h4" component="h2" flexGrow={1}>
                  {lang.selectComponents}
                </Typography>
                <Typography
                  variant="body1"
                  color="grey"
                  marginRight={1}
                  marginBottom={0}
                  paddingTop={1}>
                  {`${selectedCount} Selected`}
                </Typography>
                <Button variant="text" onClick={handleSelectAllClick}>
                  {selectAll ? lang.deselectAll : lang.selectAll}
                </Button>
              </ComponentSectionHeader>
            )}
            <StyleDivider />
            <CardGridWrapper>
              {isListLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    minHeight: '100%',
                    pb: 3,
                    boxSizing: 'border-box',
                  }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <NoResultAlert
                    isLoading={isListLoading}
                    dataToCheck={components}
                  />
                  <CardGrid
                    isLoading={isListLoading}
                    error={errorLoadingComponents}
                    updatePage={getNextComponentPage}>
                    {components.map((component: IComponent) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={component.id}
                        data-testid={DataTestID.AddComponentTagComponentData}>
                        <CheckboxLabel
                          label={`${component.name}`}
                          control={
                            <Checkbox
                              checked={selectedComponents.some(
                                (selectedComponent) =>
                                  selectedComponent.id === component.id,
                              )}
                              onChange={() =>
                                handleComponentCheckboxChange(component)
                              }
                            />
                          }
                        />
                      </Grid>
                    ))}
                  </CardGrid>
                  {isFetchingNextPage && (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </>
              )}
              <div ref={gridBottomRef}></div>
            </CardGridWrapper>
          </ComponentContainer>
        </Grid>
      </Grid>
      <SimpleDialog
        open={CloseModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.discardModal.title}
        onClose={CloseModal.onCloseModal}
        onClickCancel={CloseModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleModalConfirm}
      />
      {!isViewOnly && (
        <Footer
          showFooterActions
          onCancel={CloseModal.onOpenModal}
          onSave={editConfirmDialog.onOpenModal}
          cancelLabel={lang.close}
          disableSave={isSaveDisabled}
          isSaving={isSaving}
        />
      )}
      <SimpleDialog
        open={editConfirmDialog.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.editComponentModal.title}
        subTitle={lang.editComponentModal.subTitle}
        onClose={editConfirmDialog.onCloseModal}
        onClickCancel={editConfirmDialog.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleSave}
      />
    </Grid>
  );
};
